<script lang="ts">
import ApiEnter from "@/api/ApiEnter";
import ButtonComponent from "@/components/Common/ButtonComponent.vue";
import LoginStep from "@/components/Enter/LoginStep.vue";
import { ELoginStepYandex } from "@/struct/ELogin/ELoginStep";
import {
  IAuthErrorResponse,
  IAuthSuccessResponse,
} from "@/struct/ILogin/ILogin";
import { isAuthSuccess } from "@/util/auth";
import UtilStorage from "@/util/Common/UtilStorage";
import UtilError from "@/util/ValidateError/UtilError";
import { getWeakPasswordHint } from "@/util/ValidatePassword/UtilPassword";
import { Browser } from "@capacitor/browser";
import { Options, Vue } from "vue-class-component";
import { Watch } from "vue-property-decorator";

@Options({
  components: { LoginStep, ButtonComponent },
})
export default class LoginYandex extends Vue {
  @Watch("password")
  watchPasswordInput(value: string) {
    const MAX_PASSWORD_LENGTH = 5;

    if (value.length > MAX_PASSWORD_LENGTH) {
      this.password = value.substring(0, MAX_PASSWORD_LENGTH);
    }
  }

  get passwordWeakHint(): string | null {
    if (!this.password) {
      return null;
    }
    return getWeakPasswordHint(this.password);
  }

  get isCreateContinueEnabled(): boolean {
    if (this.password.length !== 5) {
      return false;
    }
    return this.passwordWeakHint === null;
  }

  public goToRepeatPass(): void {
    if (!this.isCreateContinueEnabled) {
      return;
    }
    this.currentStep = this.enumLoginSteps.RepeatPass;
  }

  @Watch("passwordRepeat")
  watchPasswordRepeat(value: string): void {
    const MAX_PASSWORD_LENGTH = 5;

    if (value.length === MAX_PASSWORD_LENGTH && value !== this.password) {
      this.passwordRepeatError = "Пароли не совпадают, попробуйте еще раз";
      this.passwordRepeat = "";
    }
  }

  public hasError = false;
  public describeError = "";
  public password = "";
  public passwordRepeat = "";
  public hasRequest = false;
  private activationPassword = "";

  public phoneFromMain = "";
  public phoneFromYandex = "";

  public currentStep: ELoginStepYandex = ELoginStepYandex.AuthYandex;
  public enumLoginSteps = ELoginStepYandex;

  public passwordRepeatError = "";
  public passwordError = "";

  private formatPhoneForYandex(phone: string): string {
    let cleaned = phone.replace(/\D/g, "");

    if (cleaned.startsWith("8") && cleaned.length === 11) {
      cleaned = "7" + cleaned.substring(1);
    }

    if (cleaned.length < 12) {
      cleaned = "+".repeat(12 - cleaned.length) + cleaned;
    }

    return cleaned.substring(0, 12);
  }

  private isAuthSuccess(response: any): response is IAuthSuccessResponse {
    return typeof response === "object" && "access_token" in response;
  }

  private isAuthError(response: any): response is IAuthErrorResponse {
    return typeof response === "object" && "error" in response;
  }

  private handleAuthError(response: IAuthErrorResponse) {
    this.phoneFromYandex = response.username || "";
    if (response.detail === "USER_NOT_ACTIVATE") {
      if (this.phoneFromMain !== this.phoneFromYandex) {
        this.currentStep = ELoginStepYandex.ChooseNumber;
      } else {
        this.currentStep = ELoginStepYandex.CreatePass;
      }
      this.hasError = false;
      return;
    }

    this.hasError = true;

    const oauthErrors: Record<string, string> = {
      invalid_request: "Неверный запрос авторизации",
      unauthorized_client: "Клиент не авторизован",
      access_denied: "Доступ запрещен пользователем",
    };

    const customErrors: Record<string, string> = {
      USER_NOT_EXISTS: `Номер ${this.phoneFromMain} не совпадает с привязанным ${this.phoneFromYandex}`,
      LIMITING_INPUT_ATTEMPTS: "Превышено количество попыток",
      USER_NOT_ACTIVATE: "Требуется активация аккаунта",
    };

    this.describeError =
      oauthErrors[response.error] ||
      customErrors[response.detail || ""] ||
      response.error_description ||
      "Неизвестная ошибка, обратитесь в поддержку";
  }

  private async saveAuthData(response: IAuthSuccessResponse) {
    await UtilStorage.setItem("token", response.access_token);
    await UtilStorage.setItem("token_type", response.token_type);
    // await UtilStorage.setItem("visitor_id", response.visitor_id);

    if (response.refresh_token) {
      await UtilStorage.setItem("refresh_token", response.refresh_token);
    }
  }

  public onFocusPassword(field: "password" | "passwordRepeat") {
    const input = this.$refs.input as unknown as HTMLInputElement[];
    const currentField = input[this[field].length];
    if (currentField) currentField.select();
  }

  public inputPasswordEvent(
    field: "password" | "passwordRepeat",
    value: number,
  ): void {
    this[field] += value;
    if (this[field].length < 5) {
      const input = this.$refs.input as unknown as HTMLInputElement[];
      input[this[field].length].select();
    }
  }

  public keyDownEvent(field: "password" | "passwordRepeat") {
    this[field] = this[field].substring(0, this[field].length - 1);
    const input = this.$refs.input as unknown as HTMLInputElement[];
    const currentInput = input[this[field].length];
    if (currentInput) {
      currentInput.select();
    }
  }

  public async activateUser() {
    try {
      const code = this.$route.query.code as string;
      const response = await ApiEnter.activateWithYandex(
        code,
        this.phoneFromYandex,
        this.password,
      );

      if (isAuthSuccess(response)) {
        await this.saveAuthData(response);
        await this.$router.replace({ name: "Home" });
      } else {
        this.describeError =
          UtilError.sanitizeBackendMessage(
            UtilError.backendMessageOf(response),
          ) || "Ошибка активации аккаунта";
        this.hasError = true;
      }
    } catch (error) {
      this.describeError = "Ошибка при активации";
      this.hasError = true;
    }
  }

  async created() {
    try {
      window.YaSendSuggestToken?.(window.location.origin, { flag: true });
    } catch (error) {
      console.error(error);
    }

    let access_token = null;

    if (this.$route.query.access_token) {
      access_token = this.$route.query.access_token;
    }

    if (!access_token) {
      const tempToken = await UtilStorage.getItem("yandex_temp_token");
      if (tempToken) {
        access_token = tempToken;
      }
    }

    if (!access_token && window.location.hash) {
      try {
        if (window.location.hash.includes("access_token=")) {
          const hash = window.location.hash.replace("#", "?");
          const params = new URLSearchParams(hash);
          access_token = params.get("access_token");

          if (
            !access_token &&
            window.location.hash.includes("&access_token=")
          ) {
            const hashParts = window.location.hash.split("&access_token=");
            if (hashParts.length > 1) {
              access_token = hashParts[1].split("&")[0];
            }
          }

          if (access_token && window.location.hash.includes("&access_token=")) {
            const correctedHash = window.location.hash.replace(
              "&access_token=",
              "?access_token=",
            );
            const correctedUrl = window.location.href.replace(
              window.location.hash,
              correctedHash,
            );
            window.history.replaceState({}, "", correctedUrl);

            this.$router.replace({
              path: "/login-yandex",
              query: { access_token },
            });
            return;
          }
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (!access_token && window.location.search) {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        access_token = searchParams.get("access_token");
      } catch (e) {
        console.error(e);
      }
    }

    if (!access_token && this.$PLATFORM !== "web") {
      try {
        const url = new URL(window.location.href);
        access_token =
          url.searchParams.get("access_token") ||
          url.searchParams.get("token") ||
          (url.hash.includes("access_token=")
            ? url.hash.split("access_token=")[1].split("&")[0]
            : null);
      } catch (e) {
        console.error(e);
      }
    }

    if (access_token) {
      await UtilStorage.removeItem("yandex_temp_token");
      await this.handleYandexToken(access_token);
    } else if (
      window.location.hash &&
      window.location.hash.includes("error=access_denied")
    ) {
      this.hasError = true;
      this.describeError = "Вы отменили авторизацию через Яндекс";
    } else {
      this.hasError = true;
      this.describeError = "Отсутствует access_token";
    }
  }

  private async handleYandexToken(access_token: string) {
    this.hasRequest = true;
    try {
      const phoneFromStorage = await UtilStorage.getItem("phoneRegisterYandex");
      if (!phoneFromStorage) {
        this.hasError = true;
        this.describeError = "Номер телефона не найден";
        return;
      }

      let password = this.password;
      if (!password && this.$PLATFORM !== "web") {
        password = (await UtilStorage.getItem("yandex_temp_password")) || "";
        await UtilStorage.removeItem("yandex_temp_password");
      }

      const formattedPhone = this.formatPhoneForYandex(phoneFromStorage);
      const response = await ApiEnter.postYandexLoginCallback(
        formattedPhone,
        access_token,
        password,
      );
      if (isAuthSuccess(response)) {
        await this.saveAuthData(response);
        await this.$router.replace({ name: "Home" });
        if (this.$PLATFORM === "ios") {
          try {
            await Browser.close();
          } catch (e) {
            console.error(e);
          }
        }
      } else {
        this.hasError = true;
        this.describeError =
          UtilError.sanitizeBackendMessage(
            UtilError.backendMessageOf(response),
          ) || "Ошибка авторизации через Яндекс";
      }
    } catch (error) {
      this.hasError = true;
      this.describeError = "Не удалось завершить авторизацию";
    } finally {
      this.hasRequest = false;
    }
  }
}
</script>

<template>
  <div class="login-container">
    <div class="login-form">
      <div v-auto-animate>
        <template v-if="currentStep === enumLoginSteps.ChooseNumber">
          <LoginStep :title="['Подтвердите действие']">
            <template v-slot:action>
              <p class="text-center mb-3">
                Вы хотели войти под номером {{ phoneFromMain }}, но в Яндекс
                указан номер {{ phoneFromYandex }}. <br />Продолжить
                регистрацию?
              </p>
              <div>
                <ButtonComponent
                  class="form-button mb-6"
                  font="normal"
                  rounded="md"
                  color="primary"
                  @clickAction="$router.replace({ name: 'LoginPage' })"
                  title="Вернуться к авторизации"
                />
                <ButtonComponent
                  class="form-button mb-3"
                  font="normal"
                  rounded="md"
                  color="greyLight"
                  @clickAction="currentStep = enumLoginSteps.CreatePass"
                  title="Продолжить"
                />
              </div>
            </template>
          </LoginStep>
        </template>
        <template v-if="currentStep === enumLoginSteps.AuthYandex">
          <div class="text-center" v-if="!hasError">
            <p class="text-lg">Еще немного...</p>
            <p>Завершаем авторизацию</p>
          </div>
          <div class="text-center space-y-3" v-else>
            <p class="text-lg">Произошла ошибка с авторизацией через Яндекс</p>
            <p>{{ describeError }}</p>
            <p>Попробуйте еще раз или войдите через другой метод</p>
            <ButtonComponent
              title="К авторизации"
              @clickAction="$router.replace('/')"
            />
          </div>
        </template>

        <template v-if="currentStep === enumLoginSteps.CreatePass">
          <LoginStep
            :title="['Придумайте свой пароль']"
            subtitle="Это НЕ СМС и НЕ ЗВОНОК"
            :attention="
              passwordError && passwordError.length ? passwordError : null
            "
          >
            <template v-slot:action>
              <div class="form-password">
                <div class="flex">
                  <template v-for="i in 5" :key="i">
                    <input
                      :class="{ active: password[i - 1] }"
                      @keyup.delete="keyDownEvent('password')"
                      @input="
                        inputPasswordEvent('password', $event.target.value)
                      "
                      ref="input"
                      type="password"
                      :value="password[i - 1]"
                      @focus="onFocusPassword('password')"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      autocomplete="off"
                      maxlength="1"
                      class="input-box focus:border focus:border-yellow-200"
                    />
                  </template>
                </div>

                <ButtonComponent
                  :disabled="!isCreateContinueEnabled"
                  class="form-button mb-6"
                  :loading="hasRequest"
                  font="normal"
                  type="submit"
                  rounded="md"
                  @clickAction="goToRepeatPass"
                  :color="isCreateContinueEnabled ? 'primary' : 'greyLight'"
                  title="Продолжить"
                />
              </div>
              <div
                v-if="passwordWeakHint"
                class="mt-2 text-center text-sm text-red"
              >
                {{ passwordWeakHint }}
              </div>
            </template>
          </LoginStep>
        </template>
        <template v-if="currentStep === enumLoginSteps.RepeatPass">
          <LoginStep
            :title="['Повторите пароль']"
            :attention="
              !passwordRepeat.length && passwordRepeatError
                ? passwordRepeatError
                : null
            "
          >
            <template v-slot:action>
              <div class="form-password">
                <div class="flex">
                  <template v-for="i in 5" :key="i">
                    <input
                      :class="{ active: passwordRepeat[i - 1] }"
                      ref="input"
                      @keyup.delete="keyDownEvent('passwordRepeat')"
                      @focus="onFocusPassword('passwordRepeat')"
                      :disabled="hasRequest"
                      @input="
                        inputPasswordEvent(
                          'passwordRepeat',
                          $event.target.value,
                        )
                      "
                      :value="passwordRepeat[i - 1]"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      type="password"
                      autocomplete="off"
                      maxlength="1"
                      class="input-box focus:border focus:border-yellow-200"
                    />
                  </template>
                </div>

                <ButtonComponent
                  :disabled="
                    passwordRepeat.length < 5 || passwordRepeat !== password
                  "
                  class="form-button"
                  font="normal"
                  type="submit"
                  :loading="hasRequest"
                  rounded="md"
                  :color="passwordRepeat.length === 5 ? 'primary' : 'greyLight'"
                  title="Продолжить"
                  @clickAction="activateUser"
                />
              </div>
            </template>
          </LoginStep>
        </template>
      </div>
    </div>
  </div>
</template>
